import React, { useEffect, useRef, useState } from "react";
import { apiClient } from "../api_client";
import L from "leaflet";

export default function Tracking({ eventId }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef({});
  const [points, setPoints] = useState([]);
  const [mapLoading, setMapLoading] = useState(false);

  // Load locations from API
  useEffect(() => {
    if (!eventId) return;

    const loadLocations = async () => {
      setMapLoading(true);
      const response = await apiClient.tracking.getLocations(eventId);

      const entrants = response.entrants || [];

      const pointsArray = entrants.map((data, index) => ({
        id: index,
        lat: data.lat,
        long: data.long,
        number: data.number,
        name: data.name,
        timestamp: new Date(data.time),
      }));

      setMapLoading(false);
      setPoints(pointsArray);
    };

    loadLocations();
  }, [eventId]);

  // Initialize map
  useEffect(() => {
    if (!mapRef.current) return;
    if (mapInstanceRef.current) return;
    if (mapLoading) return;
    if (points.length === 0) return;

    const map = L.map(mapRef.current).setView(
      [points[0].lat, points[0].long],
      13
    );

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
    }).addTo(map);

    mapInstanceRef.current = map;

    return () => {
      map.remove();
      mapInstanceRef.current = null;
      markersRef.current = {};
    };
  }, [mapLoading, points]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    const bounds = [];

    points.forEach((p) => {
      const position = [p.lat, p.long];
      bounds.push(position);

      const icon = L.divIcon({
        className: "numbered-marker",
        html: `<div class="marker-circle">${p.number}</div>`,
        iconSize: [32, 32],
        iconAnchor: [16, 16],
      });

      const lastSeen = p.timestamp.toLocaleTimeString();

      if (markersRef.current[p.id]) {
        markersRef.current[p.id].setLatLng(position).setIcon(icon);
        markersRef.current[p.id].getPopup().setContent(
          `<strong>${p.name}</strong><br/>Last seen: ${lastSeen}`
        );
      } else {
        const marker = L.marker(position, { icon }).addTo(map);
        marker.bindPopup(
          `<strong>${p.name}</strong><br/>Last seen: ${lastSeen}`
        );
        markersRef.current[p.id] = marker;
      }
    });

    if (bounds.length === 1) {
      map.setView(bounds[0], 15);
    } else if (bounds.length > 1) {
      map.fitBounds(bounds, { padding: [30, 30] });
    }
  }, [points]);

  return (
    <>
      {mapLoading && (
        <div style={{ textAlign: "center" }}>
          <div className="spinner-border text-primary" role="status" />
          <p style={{ marginTop: "15px" }}>Loading tracking data...</p>
        </div>
      )}

      {!mapLoading && points.length > 0 && (
        <div
          ref={mapRef}
          style={{
            height: "450px",
            borderRadius: "10px",
            marginBottom: "30px",
          }}
        />
      )}
      {/* CSS for numbered markers */}
      <style>{`
        .numbered-marker {
          background: none;
          border: none;
        }
        .marker-circle {
          width: 28px;
          height: 28px;
          background: #2563eb;
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          font-size: 14px;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        }
      `}</style>     
    </>
  );
}
